import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { baseUrl, getDateWithDay } from "../../comman/config.js";
import { MainRequirementFilter } from "../../components/Filter/MainRequirementFilter.js";
import toast, { Toaster } from "react-hot-toast";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";
import Select from 'react-select';
import Loader from '../../components/Loader'
import { formatDate } from "../../comman/config.js";
import ReturnReceiptModal from "../../components/MainStoreModal.js";
import { menuAboveTable } from "../../comman/selectMenu";

function IssueList() {
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [session, setSession] = useState({});
  const [issueStatus, setIssueStatus] = useState({});
  const [item, setItem] = useState({});
  const [kit, setKit] = useState({})
  const [kitList, setKitList] = useState([])
  const [itemIngredientList, setItemIngredientList] = useState([]);
  const [allIngredients, setAllIngredients] = useState([]);
  const [loading, setLoading] = useState(false)

  const [activeIndex, setActiveIndex] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sessionList, setSessionList] = useState([]);
  const [menuItemList, setMenuItemList] = useState([]);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Fetch all store=1 ingredients once on mount
  useEffect(() => {
    getIngredientList();
  }, [])

  useEffect(() => {
    if (fromDate && toDate) {
      getKitList();
    }
  }, [fromDate, toDate, session, item, issueStatus])

  useEffect(() => {
    if (activeIndex !== null) {
      getKitItemsList()
    }
  }, [kit, item, activeIndex])

  useEffect(() => {
    getSessionList();
    getMenuItemList();
  }, [])

  // Filter in-memory — no API call on row expand
  const ingredientList = useMemo(() => {
    const existingIds = new Set(itemIngredientList?.map(i => i.ingredient_id) ?? []);
    return allIngredients.filter(i => !existingIds.has(i.id));
  }, [allIngredients, itemIngredientList]);

  const getSessionList = async () => {
    const response = await axios.get(
      `${baseUrl}/comman/list?table=session_master&condition=status=1`
    );
    setSessionList(response.data.data);
  };

  const getMenuItemList = async () => {
    const response = await axios.get(
      `${baseUrl}/comman/list?table=item_master&condition=status=1`
    );
    setMenuItemList(response.data.data);
  };

  const handelFilterChange = async (from_date, to_date, session, item, issueStatus) => {
    setActiveIndex(null)
    setFromDate(from_date);
    setToDate(to_date);
    setSession(session);
    setItem(item);
    setIssueStatus(issueStatus)
  };

  const getIngredientList = async () => {
    const response = await axios.get(`${baseUrl}/ingredients/list?limit=all`);
    setAllIngredients(response.data.data.filter(i => i.status == 1 && i?.store?.id == 1));
  };

  const getKitItemsList = async () => {
    setLoading(true);
    const kit = kitList.find((item, index) => index == activeIndex);

    // Group clicked — fetch ingredients for each kit in the group, then aggregate
    if (kit.kits && kit.kits.length > 0) {
      const ingredientResponses = await Promise.all(
        kit.kits.map(k => {
          const cond = `pr.menu_item_id='${k.menu_item_id}' AND pr.date = '${k.date}' AND pr.session='${k.session}' AND pr.batch = '${k.batch}' AND im.store = 1`;
          return axios.get(`${baseUrl}/return_receipt/ingredient_list?condition=${cond}`);
        })
      );
      const aggregatedMap = {};
      kit.kits.forEach((k, idx) => {
        (ingredientResponses[idx].data.data ?? []).forEach(ingredient => {
          if (parseFloat(ingredient.return_qty) <= 0) return;
          const ingKey = ingredient.ingredient_id;
          if (!aggregatedMap[ingKey]) {
            aggregatedMap[ingKey] = { ...ingredient, return_qty: 0, return_receipt_qty: 0, details: [] };
          }
          aggregatedMap[ingKey].return_qty += parseFloat(ingredient.return_qty || 0);
          aggregatedMap[ingKey].return_receipt_qty += parseFloat(ingredient.return_receipt_qty || 0);
          aggregatedMap[ingKey].details.push({
            kit_no: k.kit_no, session: k.session, menu_item: k.menu_item_id,
            return_qty: ingredient.return_qty, return_receipt_qty: ingredient.return_receipt_qty, date: k.date
          });
        });
      });
      setItemIngredientList(Object.values(aggregatedMap));
      setLoading(false);
      return;
    }

    // Individual kit fallback
    const condition = `pr.menu_item_id='${kit.menu_item_id}' AND pr.date = '${kit.date}' AND pr.session='${kit.session}' AND pr.batch = '${kit.batch}' AND im.store = 1`;
    const RecipeDbData = await axios.get(`${baseUrl}/return_receipt/ingredient_list?condition=${condition}`);
    const ingredientsList = RecipeDbData.data.data;

    if (ingredientsList.length > 0) {
      const filterable = ingredientsList.filter(i => parseFloat(i.return_qty) > 0);
      const queries = filterable.map(i => ({
        key: `ird_${i.ingredient_id}`,
        table: 'item_requirement_detail',
        condition: `date = '${kit.date}' AND session = '${kit.session}' AND menu_item_id = '${kit.menu_item_id}' AND batch = '${kit.batch}' AND ingredient_id = '${i.ingredient_id}'`
      }));

      const bd = queries.length > 0
        ? (await axios.post(`${baseUrl}/comman/batch-list`, { queries })).data.data
        : {};

      const updatedIngredientsList = filterable.map(i => {
        const irdRows = bd[`ird_${i.ingredient_id}`] ?? [];
        let return_receipt_qty;
        let return_receipt_status;
        if (irdRows.length > 0) {
          return_receipt_qty = irdRows[0].return_receipt_qty;
          return_receipt_status = irdRows[0].return_receipt_status;
        }
        return {
          ...i,
          return_qty: i.return_qty ?? 0,
          return_receipt_qty: return_receipt_qty ?? 0,
          return_receipt_status: return_receipt_status
        };
      });
      setLoading(false);
      setItemIngredientList(updatedIngredientsList);
    }
  }
  const getKitList = async () => {
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (session?.value && session?.value !== 'all') params.session = session.value;
      if (item?.value && item?.value !== 'all') params.menu_item_id = item.value;
      if (issueStatus.length > 0 && issueStatus !== 'undefined') {
        const hasAll = issueStatus.some((s) => s?.value == 0);
        if (!hasAll) params.issue_statuses = issueStatus.map((s) => s?.value).join(',');
      }

      const response = await axios.get(`${baseUrl}/return_receipt/kit_list`, { params });
      const kits = response.data.data ?? [];

      // Group kits by date/session/item — ingredients loaded lazily on click
      const createGroupKey = (kit) => {
        let key = kit.date;
        if (session?.value && session?.value !== 'all') key += `_${kit.session || 'no_session'}`;
        if (item?.value && item?.value !== 'all') key += `_${kit.menu_item_id || 'no_item'}`;
        return key;
      };

      const groupedKits = {};
      kits.forEach(kit => {
        const groupKey = createGroupKey(kit);
        if (!groupedKits[groupKey]) {
          groupedKits[groupKey] = {
            date: kit.date,
            session: (session?.value && session?.value !== 'all') ? kit.session : null,
            menu_item_id: (item?.value && item?.value !== 'all') ? kit.menu_item_id : null,
            menu_item_name: (item?.value && item?.value !== 'all') ? kit.menu_item_name : null,
            return_receipt_status: kit.return_receipt_status,
            kits: [],
          };
        }
        groupedKits[groupKey].kits.push(kit);
      });

      setKitList(Object.values(groupedKits));
    } catch (error) {
      console.error('Error fetching kit list:', error);
    } finally {
      setLoading(false);
    }
  };
  const generateKitDisplayName = (kitItem) => {
    let displayName = formatDate(kitItem.date);

    let sessionName = "";
    let menuItemName = "";

    // Find session name from sessionList
    if (kitItem.session && sessionList.length > 0) {
      const matchedSession = sessionList.find(s => s.id == kitItem.session);
      if (matchedSession) {
        sessionName = matchedSession.name;
      }
    }

    // Find menu item name from menuItemList
    if (kitItem.menu_item_id && menuItemList.length > 0) {
      const matchedMenuItem = menuItemList.find(m => m.id == kitItem.menu_item_id);
      if (matchedMenuItem) {
        menuItemName = matchedMenuItem.eng_name || matchedMenuItem.item_name; // use whichever field is correct
      }
    }

    // Append session name if found
    if (sessionName) {
      displayName += `/${sessionName}`;
    }

    // Append menu item name if found
    if (menuItemName) {
      displayName += `/${menuItemName}`;
    }

    // Determine receipt status text
    const status = kitItem.return_receipt_status === 1
      ? 'Pending Received'
      : (kitItem.return_receipt_status === 2 ? "Partly Received" : "Received");

    return `${displayName} (${status})`;
  };

  const clearItem = () => {
    setActiveIndex(null)
    setItemIngredientList(null);
    getKitList()
  };

  const handleClick = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
    setKit(kitList.find((item, i) => i == index))
  };

  const handleInputChange = (index) => (e) => {
    const { name, value } = e.target;

    setItemIngredientList((prevFormData) => {
      const updatedFormData = [...prevFormData];
      updatedFormData[index] = { ...updatedFormData[index], [name]: value };
      return updatedFormData;
    });
  };

  const handleAddButtonClick = (index) => {
    setItemIngredientList((prevFormData) => {
      const updatedFormData = [...prevFormData];
      updatedFormData[index] = { ...updatedFormData[index], add_return_receipt: true };
      return updatedFormData;
    });
  }
  const handleSwitchSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(itemIngredientList.filter((item) => item.return_receipt_status == 1 || item.return_receipt_status == 2 || item.return_receipt_status == 3 || item.return_receipt_status == undefined));
    }
    setSelectAll(!selectAll);
  };

  const handleToggleSelect = (index) => {
    const updatedSelectedRows = selectedRows.includes(itemIngredientList[index])
      ? selectedRows.filter(item => item !== itemIngredientList[index])
      : [...selectedRows, itemIngredientList[index]];

    setSelectedRows(updatedSelectedRows);
  };
  // Updated handleSave function for the frontend
  const handleSave = async () => {
    if (selectedRows.length == 0) {
      alert("Please select at least one row");
      setSelectedRows([]);
      return;
    }

    if (selectedRows.some(item => item.return_receipt_qty == 0 && item.return_receipt_status == 1)) {
      alert("One or more items have an empty issue qty value.");
      setSelectedRows([]);
      return;
    }

    if (selectedRows.filter(item => item?.return_receipt_qty > 0).some(item => item.ingredient_id === null)) {
      alert("One or more items have an empty ingredient value.");
      setSelectedRows([]);
      return;
    }

    if (itemIngredientList.some(item => item.ingredient_id === null)) {
      alert("One or more items have an empty ingredient value.");
      return;
    }

    try {
      const response = await axios.post(`${baseUrl}/return_receipt/ingredient_update`, {
        ingredientsList: itemIngredientList,
        selectedRows: selectedRows,
        item: kit
      });
      toast.success("Item added successfully!");
      clearItem();
    } catch (error) {
      toast.error("Failed to add item. Please try again.");
    }
    setSelectedRows([]);
  };

  // Updated handleSaveButton function for the frontend
  const handleSaveButton = async () => {
    try {
      const response = await axios.post(`${baseUrl}/return_receipt/ingredient_save`, {
        ingredientsList: itemIngredientList,
        item: getDetailStructure(kit)
      });
      toast.success("Item saved successfully!");
      clearItem();
    } catch (error) {
      toast.error("Failed to add item. Please try again.");
    }
  };

  // Helper function to get the correct detail structure for backend
  const getDetailStructure = (kitItem) => {
    // For aggregated items, we need to handle multiple batches
    if (kitItem.kits && kitItem.kits.length > 0) {
      // Return the structure that contains all necessary information
      return {
        date: kitItem.date,
        session: kitItem.session,
        menu_item_id: kitItem.menu_item_id,
        batch: kitItem.batch || kitItem.kits[0].batch, // fallback to first kit's batch
        kits: kitItem.kits // Include all kits for reference
      };
    } else {
      // For single items
      return {
        date: kitItem.date,
        session: kitItem.session,
        menu_item_id: kitItem.menu_item_id,
        batch: kitItem.batch
      };
    }
  };

  const handleAddNewIngredientRow = () => {
    setItemIngredientList([...itemIngredientList, {
      ingredient: '',
      ingredient_obj: {},
      ingredient_id: null,
      unit: null,
      unitname: '',
      wastage: 0,
      gross_issued_qty: 0,
      return_qty: 0.01,
      gross_required_qty: 0.01,
      required_qty: 0,
      issue_status: 1,
      isNew: true
    }])
  }

  const handleNewIngredientChange = (selectedOptions, index) => {
    const updatedList = [...itemIngredientList]
    updatedList[index].ingredient_obj = selectedOptions
    updatedList[index].ingredient = selectedOptions.ingredient
    updatedList[index].ingredient_id = selectedOptions.value
    updatedList[index].unit = selectedOptions.unit
    updatedList[index].unitname = selectedOptions.unitname
    setItemIngredientList(updatedList)
  }
  // Handle clicking on aggregated ingredient to show details
  const handleIngredientClick = (ingredient) => {
    setModalData({
      date: kit.date,
      ingredient: ingredient.ingredient,
      details: ingredient.details,
      session: sessionList,
      menu_item: menuItemList
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  return (
    <div className="p-4">
      {loading && <Loader />}
      <div className="flex justify-between items-center py-4 px-6">
        <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">
          Store Return Receipt
        </h1>
        <Toaster position="top-center" reverseOrder={false} />
      </div>

      <div className="relative shadow-md sm:rounded-lg">
        <MainRequirementFilter handleFilterChange={handelFilterChange} page="receipt" />
      </div>

      <div>
        {kitList.map((item, index) => (
          <div key={index} className="border-b mt-4">
            <div
              className={`flex justify-between items-center p-4 cursor-pointer accordion-title border rounded 
              ${item.return_receipt_status == 1 ? 'bg-orange-400' :
                  (item.return_receipt_status == 2 ? 'bg-yellow-400' : 'bg-green-400')}`}
              onClick={() => handleClick(index)}>
              <span className="text-sm sm:text-base break-words">
                {generateKitDisplayName(item)}
              </span>
              {index === activeIndex ? (
                <ChevronUpIcon className="w-6 h-6 flex-shrink-0" />
              ) : (
                <ChevronDownIcon className="w-6 h-6 flex-shrink-0" />
              )}
            </div>

            {index === activeIndex && (
              <div className="space-y-2 p-4">
                <div>
                  {/* Desktop Table View */}
                  <div className="hidden lg:block">
                    <table className="w-full mt-10 text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                          <th scope="col" className="px-2 py-1 border border-black text-center">
                            <input type="checkbox" className="newrow checkboxAll" onChange={handleSwitchSelectAll} checked={selectAll} />
                          </th>
                          <th scope="col" className="px-2 py-1 border border-black text-center w-60">Ingredient</th>
                          <th scope="col" className="px-2 py-1 border border-black text-center">Returned Qty</th>
                          <th scope="col" className="px-2 py-1 border border-black text-center">Received Qty</th>
                          <th scope="col" className="px-2 py-1 border border-black text-center">Receipt Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemIngredientList?.map((item, index) => (
                          <tr key={index} className="border border-black-b">
                            {(((item.return_receipt_status !== 3) || (item.return_receipt_status === 3 && item.add_return_receipt)) && ((item.return_receipt_status !== 2) || (item.return_receipt_status === 2 && item.add_return_receipt))) ? (
                              <td className="px-2 py-1 border border-black text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedRows.includes(item)}
                                  onChange={() => handleToggleSelect(index)}
                                />
                              </td>
                            ) : (
                              <td className="px-2 py-1 border border-black text-center"></td>
                            )}

                            {item.isNew ? (
                              <td className="px-2 py-1 border border-black text-center">
                                <Select
                                  {...menuAboveTable}
                                  classNamePrefix="select"
                                  className='basic-multi-select'
                                  value={item.ingredient_obj}
                                  name="ingredient"
                                  onChange={(selectedOptions) => handleNewIngredientChange(selectedOptions, index)}
                                  options={ingredientList.map((item) => ({
                                    value: item.id,
                                    label: `${item.ingredient} (${item.eng_name})`,
                                    unit: item.unit?.id,
                                    unitname: item.unit?.name,
                                    ingredient: `${item.ingredient}`,
                                    eng_name: item.eng_name,
                                  }))}
                                  placeholder="Ingredient List"
                                  filterOption={(option, inputValue) => {
                                    return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                                  }}
                                />
                              </td>
                            ) : (
                              <td className="px-2 py-1 border border-black text-center">{item.ingredient}</td>
                            )}

                            <td className="px-2 py-1 border border-black text-center">
                              <button
                                onClick={() => handleIngredientClick(item)}
                                className="text-blue-500 hover:text-blue-700 underline cursor-pointer"
                              >
                                {Number(item?.return_qty).toFixed(2) ?? 0}
                              </button>
                            </td>

                            <td className="px-2 py-1 border border-black text-center">
                              <input type="number"
                                id="return_receipt_qty"
                                name="return_receipt_qty"
                                value={item?.return_receipt_qty}
                                onWheel={(e) => e.target.blur()}
                                onChange={handleInputChange(index)}
                                onKeyDown={(e) => {
                                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                    e.preventDefault();
                                  }
                                }}
                                className="px-2 py-1 border rounded-md w-20 bg-orange-50"
                              />
                              {(item.return_receipt_status === 3 || item.return_receipt_status === 2) && (
                                <>
                                  <button
                                    onClick={() => handleAddButtonClick(index)}
                                    className='px-1 rounded border mx-2 scale-150 hover:bg-gray-300'
                                  >
                                    &#x2b;
                                  </button>
                                  {item.add_return_receipt && (
                                    <input
                                      type="number"
                                      id="add_return_receipt_qty"
                                      name="add_return_receipt_qty"
                                      value={item?.add_return_receipt_qty ?? ''}
                                      onWheel={(e) => e.target.blur()}
                                      onChange={handleInputChange(index)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                          e.preventDefault();
                                        }
                                      }}
                                      className="px-2 py-1 border rounded-md w-20 bg-orange-50"
                                    />
                                  )}
                                </>
                              )}
                            </td>

                            <td className="px-2 py-1 border border-black text-center">
                              {item.return_receipt_status == 3 ? "Received" : (item.return_receipt_status == 2 ? "Partly Received" : "Pending")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="block lg:hidden">
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Select All</span>
                        <input type="checkbox" className="newrow checkboxAll" onChange={handleSwitchSelectAll} checked={selectAll} />
                      </div>
                    </div>

                    {itemIngredientList?.map((item, index) => (
                      <div key={index} className="border rounded-lg p-3 mb-3 bg-white shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            {item.isNew ? (
                              <Select
                                {...menuAboveTable}
                                classNamePrefix="select"
                                className='basic-multi-select'
                                value={item.ingredient_obj}
                                name="ingredient"
                                onChange={(selectedOptions) => handleNewIngredientChange(selectedOptions, index)}
                                options={ingredientList.map((item) => ({
                                  value: item.id,
                                  label: `${item.ingredient} (${item.eng_name})`,
                                  unit: item.unit?.id,
                                  unitname: item.unit?.name,
                                  ingredient: `${item.ingredient}`,
                                  eng_name: item.eng_name,
                                }))}
                                placeholder="Ingredient List"
                                filterOption={(option, inputValue) => {
                                  return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                                }}
                              />
                            ) : (
                              <div className="font-medium text-sm">{item.ingredient}</div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              Status: {item.return_receipt_status == 3 ? "Received" : (item.return_receipt_status == 2 ? "Partly Received" : "Pending")}
                            </div>
                          </div>
                          {(((item.return_receipt_status !== 3) || (item.return_receipt_status === 3 && item.add_return_receipt)) && ((item.return_receipt_status !== 2) || (item.return_receipt_status === 2 && item.add_return_receipt))) && (
                            <input
                              type="checkbox"
                              checked={selectedRows.includes(item)}
                              onChange={() => handleToggleSelect(index)}
                              className="ml-2"
                            />
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-2 text-sm mb-3">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Returned Qty:</span>
                            <button
                              onClick={() => handleIngredientClick(item)}
                              className="text-blue-500 hover:text-blue-700 underline cursor-pointer font-medium"
                            >
                              {Number(item?.return_qty).toFixed(2) ?? 0}
                            </button>
                          </div>
                        </div>

                        <div className="mt-3">
                          <div className="text-xs text-gray-500 mb-1">Received Qty</div>
                          <div className="flex items-center gap-2">
                            <input type="number"
                              id="return_receipt_qty"
                              name="return_receipt_qty"
                              value={item?.return_receipt_qty}
                              onWheel={(e) => e.target.blur()}
                              onChange={handleInputChange(index)}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                  e.preventDefault();
                                }
                              }}
                              className="px-2 py-1 border rounded-md w-20 bg-orange-50"
                            />
                            {(item.return_receipt_status === 3 || item.return_receipt_status === 2) && (
                              <>
                                <button
                                  onClick={() => handleAddButtonClick(index)}
                                  className='px-2 py-1 rounded border hover:bg-gray-300 text-sm'
                                >
                                  +
                                </button>
                                {item.add_return_receipt && (
                                  <input
                                    type="number"
                                    id="add_return_receipt_qty"
                                    name="add_return_receipt_qty"
                                    value={item?.add_return_receipt_qty ?? ''}
                                    onWheel={(e) => e.target.blur()}
                                    onChange={handleInputChange(index)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                        e.preventDefault();
                                      }
                                    }}
                                    className="px-2 py-1 border rounded-md w-20 bg-orange-50"
                                  />
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  {kit.issue_status !== 3 && (
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      {((kit.return_receipt_status !== 3) && (kit.return_receipt_status !== 2)) && (
                        <button
                          className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
                          type="button"
                          onClick={() => handleSaveButton()}
                        >
                          Save
                        </button>
                      )}
                      <button
                        className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
                        type="button"
                        disabled={kit.issue_status == 3}
                        onClick={() => handleSave()}
                      >
                        Submit for Return
                      </button>
                      <button
                        className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
                        type="button"
                        disabled={kit.issue_status === 3}
                        onClick={() => { handleAddNewIngredientRow() }}
                      >
                        Add Ingredient
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <ReturnReceiptModal
        isOpen={isModalOpen}
        onClose={closeModal}
        modalData={modalData}
      />
    </div>
  );
}

export default IssueList;
